import { useCallback, useEffect, useRef, useState } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import IconButton from 'fe-design-base/molecules/IconButton';
import CircularInfinite from 'fe-design-base/molecules/Loaders/CircularInfinite';

import { getRequestDetails } from 'features/notifications/api';
import { CollapsibleCard } from 'features/notifications/components/CollapsibleCard';
import { ShiftCard } from 'features/notifications/components/ShiftCard';
import {
  TradeClaim,
  TradeDetailsData,
  TradeDetailsProps,
  TradeInvited,
} from 'features/notifications/types';
import { calculateScrollHeightWithRefs } from 'features/notifications/utils';

import { toI18n } from 'util/i18n';

import { NoResponders } from '../../../NoResponders';

import { TradeInvitedCard } from './TradeInvitedCard';
import { TradeShiftCard } from './TradeShiftCard';

const TradeDetails = ({
  requestId,
  onClose,
  isTeamRequest,
}: TradeDetailsProps) => {
  const [requestData, setRequestData] = useState<TradeDetailsData | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const shiftCardRef = useRef<HTMLElement>(null);
  const [scrollHeight, setScrollHeight] = useState('100vh');
  const [showResponders, setShowResponders] = useState(true);
  const [showInvited, setShowInvited] = useState(false);

  useEffect(() => {
    getRequestDetails(requestId, isTeamRequest ? 'team' : 'my').then(
      setRequestData
    );
  }, [requestId, isTeamRequest]);

  useEffect(() => {
    const handleCalculateScrollHeight = () => {
      calculateScrollHeightWithRefs(headerRef, shiftCardRef, setScrollHeight);
    };

    handleCalculateScrollHeight();

    window.addEventListener('resize', handleCalculateScrollHeight);

    return () => {
      window.removeEventListener('resize', handleCalculateScrollHeight);
    };
  }, []);

  const handleToggleResponders = useCallback(() => {
    setShowResponders(prev => !prev);
  }, []);

  const handleToggleInvited = useCallback(() => {
    setShowInvited(prev => !prev);
  }, []);

  if (!requestData) {
    return (
      <Box column h="100vh" vcenter hcenter>
        <CircularInfinite size="large" />
      </Box>
    );
  }

  const {
    shift,
    location,
    requests,
    requester,
    viewer_role: viewerRole = 'oam',
  } = requestData;

  const name = requester
    ? `${requester.first_name} ${requester.last_name}`
    : '';
  const { claims, invited } = requests;

  return (
    <Box column h="100%">
      <Box
        ref={headerRef}
        flex
        row
        alignItemsCenter
        justifyContentSpaceBetween
        bbw={2}
        bcolor="mono300"
        p={16}
        ph={32}
      >
        <IconButton
          icon="ArrowLeft"
          label={toI18n('actions.go_back')}
          onClick={onClose}
        />
        <Text variant="bodyBold" i18n="requests_feed.trade.details.title" />
        <div style={{ width: '40px' }} />
      </Box>

      <Box ref={shiftCardRef}>
        <ShiftCard shift={shift} location={location} name={name} />
      </Box>

      <Box scrollY h={scrollHeight} grow>
        <Box flex column gap={8} p={16} bgcolor="mono100">
          <CollapsibleCard
            title="claims"
            count={claims?.length || 0}
            show={showResponders}
            handleToggle={handleToggleResponders}
          >
            {showResponders && (
              <Box column gap={8}>
                {claims &&
                  claims.length > 0 &&
                  claims.map((claim: TradeClaim) => (
                    <TradeShiftCard
                      claim={claim}
                      key={claim.id}
                      shift={shift}
                      location={location}
                      requester={requester!}
                      viewer_role={viewerRole}
                    />
                  ))}
                {claims && claims.length === 0 && <NoResponders type="trade" />}
              </Box>
            )}
          </CollapsibleCard>
          <CollapsibleCard
            title="invited"
            count={invited?.length || 0}
            show={showInvited}
            handleToggle={handleToggleInvited}
          >
            {showInvited && (
              <Box column gap={8}>
                {invited &&
                  invited.length > 0 &&
                  invited.map((invitedUser: TradeInvited) => (
                    <TradeInvitedCard
                      key={invitedUser.id}
                      invitedUser={invitedUser}
                    />
                  ))}
              </Box>
            )}
          </CollapsibleCard>
        </Box>
      </Box>
    </Box>
  );
};

export default TradeDetails;
